import React, { useState, useEffect, useRef } from 'react';
import html2canvas from 'html2canvas';
import {
  Box,
  FormControl,
  Select,
  MenuItem,
  TextField,
  Button,
  Typography,
  Grid,
  Paper,
  IconButton,
  Dialog,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Divider,
  Chip,
  Card,
  CardContent,
  CardActions,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  DialogContentText,
  Alert,
  CircularProgress,
  Snackbar,
  Alert as MuiAlert,
  Autocomplete,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import HistoryIcon from '@mui/icons-material/History';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { useAuth } from '../../contexts/AuthContext';
import api, { evaluationApi } from '../../services/api';
import { evaluationDatabaseApi } from '../../services/evaluationDatabaseApi';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const StudentEvaluation = () => {
  const [selectedBooking, setSelectedBooking] = useState('no_booking');
  const [selectedStudent, setSelectedStudent] = useState('');
  // 修改 evaluation 狀態結構
  const [evaluation, setEvaluation] = useState({
    target: '',                    // 目標（可空白）
    environment: '',              // 環境
    equipment: '',                // 裝備
    stance: '',                   // 站姿
    problem_phase: '',            // 問題-階段
    problem_performance: '',      // 問題-表現
    problem_movement: '',         // 問題-動作
    improvement_target: '',       // 改善目標
    improvement_exercise: '',     // 改善-練習（可空白）
    improvement_movement: '',     // 改善-動作
    improvement_movement_detail: [], // 改善-動作細節（改為陣列）
    comments: '',                 // 評語
    actionKey: [],                // 添加 actionKey 字段
  });
  const [bookings, setBookings] = useState([]);
  const [openReport, setOpenReport] = useState(false);
  const [openHistory, setOpenHistory] = useState(false);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [evaluationHistory, setEvaluationHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [completionNotes, setCompletionNotes] = useState('');
  const [openCompletedBookings, setOpenCompletedBookings] = useState(false);
  const [completedBookings, setCompletedBookings] = useState([]);
  const [completedBookingsLoading, setCompletedBookingsLoading] = useState(false);
  const reportRef = useRef(null);
  const { user } = useAuth();

  // 新增狀態來存儲資料庫選項
  const [databaseOptions, setDatabaseOptions] = useState({
    environments: [],
    equipment: [],
    stances: [],
    problemPhases: [],
    problemPerformances: [],
    problemMovements: [],
    improvementExercises: [],
    improvementMovements: [],
    improvementMovementDetails: []
  });

  // 新增狀態存儲 Improvements 完整資料
  const [improvementsData, setImprovementsData] = useState([]);

  // 新增狀態
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' // 'success', 'error', 'warning', 'info'
  });

  // 顯示通知的函數
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  // 關閉 Snackbar
  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // 載入可評估的預約記錄
  const loadAvailableBookings = async () => {
    setBookingsLoading(true);
    try {
      const data = await evaluationApi.getAvailableBookings();
      setBookings(data.bookings || []);
    } catch (error) {
      console.error('載入預約記錄失敗:', error);
      showSnackbar('載入預約記錄失敗', 'error');
      setBookings([]); // 確保在錯誤時設置為空數組
    } finally {
      setBookingsLoading(false);
    }
  };

  // 載入已完成的預約
  const loadCompletedBookings = async () => {
    setCompletedBookingsLoading(true);
    try {
      const data = await evaluationApi.getAvailableBookings();
        const completedBookings = (data.bookings || []).filter(booking => booking.is_completed);
        
        // 按課程日期排序，最近的日期在上面
        const sortedBookings = completedBookings.sort((a, b) => {
          const dateA = new Date(a.date);
          const dateB = new Date(b.date);
          return dateB - dateA; // 降序排列，最新的在前
        });
        
        setCompletedBookings(sortedBookings);
    } catch (error) {
      console.error('載入已完成預約失敗:', error);
      showSnackbar('載入已完成預約失敗', 'error');
      setCompletedBookings([]); // 確保在錯誤時設置為空數組
    } finally {
      setCompletedBookingsLoading(false);
    }
  };

  // 創建一個安全的 API 調用函數
  const safeApiCall = async (apiFunction, defaultValue = []) => {
    try {
      const result = await apiFunction();
      return Array.isArray(result) ? result : defaultValue;
    } catch (error) {
      console.error('API 調用失敗:', error);
      return defaultValue;
    }
  };

  // 修改 loadDatabaseOptions 函數，添加錯誤處理和默認值
  const loadDatabaseOptions = async () => {
    try {
      const [
        environmentsRes,
        equipmentRes,
        stancesRes,
        problemCategoriesRes,
        improvementCategoriesRes,
        movementDetailsRes,
        improvementsRes
      ] = await Promise.all([
        evaluationDatabaseApi.getEnvironments().catch((error) => {
          console.error('載入環境失敗:', error);
          return [];
        }),
        evaluationDatabaseApi.getEquipment().catch((error) => {
          console.error('載入裝備失敗:', error);
          return [];
        }),
        evaluationDatabaseApi.getStances().catch((error) => {
          console.error('載入站姿失敗:', error);
          return [];
        }),
        evaluationDatabaseApi.getProblemCategories().catch((error) => {
          console.error('載入問題類別失敗:', error);
          return [];
        }),
        evaluationDatabaseApi.getImprovementCategories().catch((error) => {
          console.error('載入改善類別失敗:', error);
          return [];
        }),
        evaluationDatabaseApi.getAllMovementDetails().catch((error) => {
          console.error('載入動作細節失敗:', error);
          return [];
        }),
        evaluationDatabaseApi.getImprovements().catch((error) => {
          console.error('載入改善項目失敗:', error);
          console.error('錯誤狀態:', error.response?.status);
          console.error('錯誤詳情:', error.response?.data);
          return [];
        })
      ]);

      // 確保所有響應都是數組
      const safeProblemCategories = Array.isArray(problemCategoriesRes) ? problemCategoriesRes : [];
      const safeImprovementCategories = Array.isArray(improvementCategoriesRes) ? improvementCategoriesRes : [];

      // 存儲完整的 improvements 資料
      setImprovementsData(Array.isArray(improvementsRes) ? improvementsRes : []);

      setDatabaseOptions({
        environments: Array.isArray(environmentsRes) ? environmentsRes : [],
        equipment: Array.isArray(equipmentRes) ? equipmentRes : [],
        stances: Array.isArray(stancesRes) ? stancesRes : [],
        problemPhases: safeProblemCategories.filter(item => item.category_type === 'phase'),
        problemPerformances: safeProblemCategories.filter(item => item.category_type === 'performance'),
        problemMovements: safeProblemCategories.filter(item => item.category_type === 'movement'),
        improvementExercises: safeImprovementCategories.filter(item => item.category_type === 'exercise'),
        improvementMovements: safeImprovementCategories.filter(item => item.category_type === 'movement'),
        improvementMovementDetails: Array.isArray(movementDetailsRes) ? movementDetailsRes : []
      });
    } catch (error) {
      console.error('載入資料庫選項失敗:', error);
      console.error('錯誤詳情:', error.response?.data || error.message);
      showSnackbar('載入資料庫選項失敗', 'error');
      
      // 設置默認的空數組
      setDatabaseOptions({
        environments: [],
        equipment: [],
        stances: [],
        problemPhases: [],
        problemPerformances: [],
        problemMovements: [],
        improvementExercises: [],
        improvementMovements: [],
        improvementMovementDetails: []
      });
    }
  };

  // 在 useEffect 中載入資料庫選項
  useEffect(() => {
    loadAvailableBookings();
    loadDatabaseOptions();
  }, []);

  // 處理預約選擇
  const handleBookingChange = (event) => {
    const bookingId = event.target.value;
    setSelectedBooking(bookingId);
    
    if (bookingId === 'no_booking') {
      // 選擇無預約時，清空學生姓名，讓用戶手動輸入
      setSelectedStudent('');
    } else {
      // 根據選擇的預約自動填充學生姓名
      const selectedBookingData = bookings.find(booking => booking.id == bookingId);
      if (selectedBookingData) {
        setSelectedStudent(selectedBookingData.student_name);
      }
    }
  };

  // 標記預約評估為完成
  const handleMarkCompleted = async () => {
    if (!selectedBooking || selectedBooking === 'no_booking') {
      showSnackbar('無預約模式無法標記完成', 'warning');
      return;
    }
    
    try {
      await evaluationApi.markBookingCompleted(selectedBooking, completionNotes);
      showSnackbar('預約評估已標記為完成', 'success');
      
      // 重新載入預約列表
      const data = await evaluationApi.getAvailableBookings();
      const updatedBookings = data.bookings || [];
      setBookings(updatedBookings);
      setCompletionNotes('');
      
      // 自動選擇下一個未完成的預約記錄
      const incompleteBookings = updatedBookings.filter(booking => !booking.is_completed);
      const nextBooking = incompleteBookings.find(booking => booking.id != selectedBooking);
      
      if (nextBooking) {
        setSelectedBooking(nextBooking.id);
        setSelectedStudent(nextBooking.student_name);
        showSnackbar(`已自動選擇下一個預約：${nextBooking.student_name}`, 'info');
      } else {
        // 沒有下一個預約，清空選擇
        setSelectedBooking('');
        setSelectedStudent('');
        showSnackbar('沒有更多未完成的預約記錄', 'info');
      }
      
    } catch (error) {
      console.error('標記完成失敗:', error);
      showSnackbar('標記完成失敗，請稍後再試', 'error');
    }
  };

  // 將已完成改回未完成
  const handleMarkIncomplete = async (bookingId) => {
    try {
      await evaluationApi.markBookingIncomplete(bookingId);
      showSnackbar('預約已標記為未完成', 'success');
      loadCompletedBookings(); // 重新載入已完成預約列表
      loadAvailableBookings(); // 重新載入未完成預約列表
    } catch (error) {
      console.error('標記未完成失敗:', error);
      showSnackbar('標記未完成失敗，請稍後再試', 'error');
    }
  };

  // 載入過往紀錄
  const loadEvaluationHistory = async () => {
    setHistoryLoading(true);
    try {
      const response = await api.get('/evaluations/');
      setEvaluationHistory(response.data.results || response.data);
    } catch (error) {
      console.error('載入過往紀錄失敗:', error);
      showSnackbar('載入過往紀錄失敗', 'error');
    } finally {
      setHistoryLoading(false);
    }
  };

  // 載入特定評估紀錄
  const loadEvaluation = (evaluationData) => {
    setSelectedBooking(evaluationData.booking_id || 'no_booking');
    setSelectedStudent(evaluationData.student_name);
    setEvaluation({
      target: evaluationData.target || '',
      environment: evaluationData.environment || '',
      equipment: evaluationData.equipment || '',
      stance: evaluationData.stance || '',
      problem_phase: evaluationData.problem_phase || '',
      problem_performance: evaluationData.problem_performance || '',
      problem_movement: evaluationData.problem_movement || '',
      improvement_target: evaluationData.improvement_target || '',
      improvement_exercise: evaluationData.improvement_exercise || '',
      improvement_movement: evaluationData.improvement_movement || '',
      improvement_movement_detail: Array.isArray(evaluationData.improvement_movement_detail)
        ? evaluationData.improvement_movement_detail
        : (evaluationData.improvement_movement_detail ? [evaluationData.improvement_movement_detail] : []),
      comments: evaluationData.comments || '',
      actionKey: evaluationData.actionKey || [], // 添加 actionKey 載入
    });
    setOpenHistory(false);
  };

  // 刪除評估紀錄
  const handleDeleteRecord = (record) => {
    setRecordToDelete(record);
    setOpenDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!recordToDelete) return;
    
    setDeleteLoading(true);
    try {
      await api.delete(`/evaluations/${recordToDelete.id}/`);
      // 重新載入過往紀錄和預約記錄
      await loadEvaluationHistory();
      await loadAvailableBookings();
      setOpenDeleteConfirm(false);
      setRecordToDelete(null);
      showSnackbar('評估紀錄已刪除', 'success');
    } catch (error) {
      console.error('刪除失敗:', error);
      showSnackbar('刪除失敗，請稍後再試', 'error');
    } finally {
      setDeleteLoading(false);
    }
  };

  const cancelDelete = () => {
    setOpenDeleteConfirm(false);
    setRecordToDelete(null);
  };

  const handleEvaluationChange = (field, value) => {
    setEvaluation(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleActionKeyChange = (index, value) => {
    const newActionKeys = [...(evaluation.actionKey || [])];
    newActionKeys[index] = value;
    setEvaluation(prev => ({
      ...prev,
      actionKey: newActionKeys
    }));
  };

  const addActionKey = () => {
    setEvaluation(prev => ({
      ...prev,
      actionKey: [...(prev.actionKey || []), '']
    }));
  };

  const removeActionKey = (index) => {
    if ((evaluation.actionKey || []).length > 1) {
      const newActionKeys = (evaluation.actionKey || []).filter((_, i) => i !== index);
      setEvaluation(prev => ({
        ...prev,
        actionKey: newActionKeys
      }));
    }
  };

  const handleScreenshot = async () => {
    if (reportRef.current) {
      try {
        const canvas = await html2canvas(reportRef.current, {
          scale: 2,
          backgroundColor: '#ffffff',
        });
        
        const image = canvas.toDataURL('image/png');
        const link = document.createElement('a');
        link.download = `學生評估報告_${selectedStudent}.png`;
        link.href = image;
        link.click();
      } catch (error) {
        showSnackbar('截圖失敗，請稍後再試', 'error');
      }
    }
  };

  // 保存並截圖
  const handleSaveAndScreenshot = async () => {
    try {
      // 先保存到資料庫
      await handleSubmit({ preventDefault: () => {} });
      // 然後截圖
      setTimeout(() => {
        handleScreenshot();
        // 截圖完成後關閉對話框
        setOpenReport(false);
      }, 500); // 等待保存完成後再截圖
    } catch (error) {
      showSnackbar('保存失敗，請稍後再試', 'error');
    }
  };

  // 生成預覽（不存到資料庫）
  const handlePreview = (e) => {
    e.preventDefault();
    
    // 移除當前焦點以避免 aria-hidden 警告
    if (document.activeElement) {
      document.activeElement.blur();
    }
    
    // 檢查必選欄位
    const requiredFields = {
      '學生姓名': selectedStudent,
      '環境': evaluation.environment,
      '裝備': evaluation.equipment,
      '站姿': evaluation.stance,
      '問題-階段': evaluation.problem_phase,
      '問題-表現': evaluation.problem_performance,
      '問題-動作': evaluation.problem_movement,
      '改善-動作': evaluation.improvement_movement,
      '改善-動作細節': evaluation.improvement_movement_detail,
      '評語': evaluation.comments
    };
    
    const missingFields = Object.entries(requiredFields)
      .filter(([field, value]) => {
        if (field === '改善-動作細節') {
          return !Array.isArray(value) || value.length === 0;
        }
        return !value || (typeof value === 'string' && value.trim() === '');
      })
      .map(([field]) => field);
    
    if (missingFields.length > 0) {
      showSnackbar(`請填寫必選欄位：${missingFields.join('、')}`, 'warning');
      return;
    }

    // 直接打開預覽對話框，不存到資料庫
    setOpenReport(true);
    showSnackbar('預覽已生成，您可以保存並截圖', 'success');
  };

  // 提交評估到資料庫
  const handleSubmit = async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    
    // 檢查必選欄位
    const requiredFields = {
      '學生姓名': selectedStudent,
      '環境': evaluation.environment,
      '裝備': evaluation.equipment,
      '站姿': evaluation.stance,
      '問題-階段': evaluation.problem_phase,
      '問題-表現': evaluation.problem_performance,
      '問題-動作': evaluation.problem_movement,
      '改善-動作': evaluation.improvement_movement,
      '改善-動作細節': evaluation.improvement_movement_detail,
      '評語': evaluation.comments
    };
    
    const missingFields = Object.entries(requiredFields)
      .filter(([field, value]) => {
        if (field === '改善-動作細節') {
          return !Array.isArray(value) || value.length === 0;
        }
        return !value || (typeof value === 'string' && value.trim() === '');
      })
      .map(([field]) => field);
    
    if (missingFields.length > 0) {
      showSnackbar(`請填寫必選欄位：${missingFields.join('、')}`, 'warning');
      return;
    }

    setLoading(true);
    try {
      await api.post('/evaluations/', {
        student: 0,
        student_name: selectedStudent,
        booking_id: selectedBooking === 'no_booking' ? null : selectedBooking, // 無預約時設為 null
        target: evaluation.target,
        environment: evaluation.environment,
        equipment: evaluation.equipment,
        stance: evaluation.stance,
        problem_phase: evaluation.problem_phase,
        problem_performance: evaluation.problem_performance,
        problem_movement: evaluation.problem_movement,
        improvement_target: evaluation.improvement_target,
        improvement_exercise: evaluation.improvement_exercise,
        improvement_movement: evaluation.improvement_movement,
        improvement_movement_detail: evaluation.improvement_movement_detail, // 直接提交陣列
        comments: evaluation.comments,
        actionKey: evaluation.actionKey,
      });
      setOpenReport(true);
      showSnackbar('評估已保存到資料庫！', 'success');
      // 重新載入過往紀錄和預約記錄
      loadEvaluationHistory();
      loadAvailableBookings();
    } catch (error) {
      showSnackbar(error.response?.data?.detail || '提交失敗，請稍後再試', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseReport = () => {
    setOpenReport(false);
  };

  const handleOpenHistory = () => {
    // 移除當前焦點以避免 aria-hidden 警告
    if (document.activeElement) {
      document.activeElement.blur();
    }
    setOpenHistory(true);
    loadEvaluationHistory();
  };

  const handleCloseHistory = () => {
    setOpenHistory(false);
  };

  const handleOpenCompletedBookings = () => {
    // 移除當前焦點以避免 aria-hidden 警告
    if (document.activeElement) {
      document.activeElement.blur();
    }
    setOpenCompletedBookings(true);
    loadCompletedBookings();
  };

  const handleCloseCompletedBookings = () => {
    setOpenCompletedBookings(false);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('zh-TW', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // 創建詳細的 tooltip 內容
  const createTooltipContent = (record) => (
    <Box sx={{ maxWidth: 500, p: 1 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
        {record.student_name}
      </Typography>
      
      {/*record.booking_id && (
        <Typography variant="body2" sx={{ mb: 1 }}>
          <strong>預約ID：</strong>{record.booking_id}
        </Typography>
      )*/}
      
      {record.course_date && (
        <Typography variant="body2" sx={{ mb: 1 }}>
          <strong>課程日期：</strong>{new Date(record.course_date).toLocaleDateString('zh-TW')}
        </Typography>
      )}
      
      {/*record.course_time_slot && (
      <Typography variant="body2" sx={{ mb: 1 }}>
          <strong>課程時段：</strong>{record.course_time_slot}
      </Typography>
      )*/}
      
      {record.course_location && (
      <Typography variant="body2" sx={{ mb: 1 }}>
          <strong>雪場：</strong>{record.course_location}
      </Typography>
      )}
      
      {record.course_type && (
      <Typography variant="body2" sx={{ mb: 1 }}>
          <strong>課程類型：</strong>{record.course_type}
        </Typography>
      )}
      {record.target && (
        <Typography variant="body2" sx={{ mb: 1 }}>
          <strong>目標：</strong>{record.target}
        </Typography>
      )}
      <Typography variant="body2" sx={{ mb: 1 }}>
        <strong>環境：</strong>{record.environment}
      </Typography>
      
      <Typography variant="body2" sx={{ mb: 1 }}>
        <strong>裝備：</strong>{record.equipment}
      </Typography>
      
      <Typography variant="body2" sx={{ mb: 1 }}>
        <strong>站姿：</strong>{record.stance}
      </Typography>
      
      <Typography variant="body2" sx={{ mb: 1 }}>
        <strong>問題-階段：</strong>{record.problem_phase}
      </Typography>
      
      <Typography variant="body2" sx={{ mb: 1 }}>
        <strong>問題-表現：</strong>{record.problem_performance}
      </Typography>
      
      <Typography variant="body2" sx={{ mb: 1 }}>
        <strong>問題-動作：</strong>{record.problem_movement}
      </Typography>
      
      {record.improvement_target && (
        <Typography variant="body2" sx={{ mb: 1 }}>
          <strong>改善目標：</strong>{record.improvement_target}
        </Typography>
      )}
      
      {record.improvement_exercise && (
        <Typography variant="body2" sx={{ mb: 1 }}>
          <strong>改善-練習：</strong>{record.improvement_exercise}
        </Typography>
      )}
      
      <Typography variant="body2" sx={{ mb: 1 }}>
        <strong>改善-動作：</strong>{record.improvement_movement}
      </Typography>
      
      <Typography variant="body2" sx={{ mb: 1 }}>
        <strong>改善-動作細節：</strong>
      </Typography>
      {Array.isArray(record.improvement_movement_detail) ? (
        record.improvement_movement_detail.map((detail, index) => (
          <Typography key={index} variant="body2" sx={{ pl: 2, mb: 0.5 }}>
            • {detail}
          </Typography>
        ))
      ) : (
        <Typography variant="body2" sx={{ pl: 2 }}>
          {record.improvement_movement_detail || '無'}
        </Typography>
      )}
      
      {record.actionKey && record.actionKey.length > 0 && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
            動作要領：
          </Typography>
          {record.actionKey.map((key, index) => (
            <Typography key={index} variant="body2" sx={{ pl: 1 }}>
              • {key}
            </Typography>
          ))}
        </Box>
      )}
      
      <Typography variant="body2" component="div" sx={{ whiteSpace: 'pre-wrap' }}>
        <strong>評語：</strong>{record.comments}
      </Typography>
    </Box>
  );

  // 在組件頂部添加一個安全的過濾函數
  const getIncompleteBookings = () => {
    return (bookings || []).filter(booking => !booking.is_completed);
  };

  // 根據選擇的改善-動作獲取相關的動作細節選項
  const getFilteredMovementDetails = () => {
    if (!evaluation.improvement_movement) {
      return databaseOptions.improvementMovementDetails;
    }
    
    // 根據 category_name 來匹配，因為用戶選擇的是類別名稱
    const matchedImprovement = improvementsData.find(
      imp => imp.category_name === evaluation.improvement_movement && imp.category_type === 'movement'
    );
    
    if (matchedImprovement && matchedImprovement.improvement_movement_details && matchedImprovement.improvement_movement_details.length > 0) {
      // 返回 improvement_movement_details 物件陣列，包含 movement_detail 屬性
      return matchedImprovement.improvement_movement_details.map(item => item.movement_detail);
    }
    
    return databaseOptions.improvementMovementDetails;
  };

  // 獲取所有可用的動作細節選項（包括預設選項和用戶已選擇的選項）
  const getAllMovementDetailOptions = () => {
    const filteredDetails = getFilteredMovementDetails();
    const currentDetails = evaluation.improvement_movement_detail || [];
    
    // 合併預設選項和用戶已選擇的選項
    const allOptions = [...filteredDetails.map(detail => detail.name), ...currentDetails];
    
    // 去重並返回
    return [...new Set(allOptions)];
  };

  // 編輯動作細節的函數
  const editMovementDetail = (index, newValue) => {
    const currentDetails = [...(evaluation.improvement_movement_detail || [])];
    currentDetails[index] = newValue;
    handleEvaluationChange('improvement_movement_detail', currentDetails);
  };

  // 編輯對話框狀態
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(-1);
  const [editingValue, setEditingValue] = useState('');
  
  // 控制動作細節下拉選單的開啟狀態
  const [movementDetailOpen, setMovementDetailOpen] = useState(false);

  // 打開編輯對話框
  const openEditDialog = (index, currentValue) => {
    setEditingIndex(index);
    setEditingValue(currentValue);
    setEditDialogOpen(true);
  };

  // 關閉編輯對話框
  const closeEditDialog = () => {
    setEditDialogOpen(false);
    setEditingIndex(-1);
    setEditingValue('');
  };

  // 保存編輯
  const saveEdit = () => {
    if (editingValue.trim() !== '') {
      editMovementDetail(editingIndex, editingValue.trim());
    }
    closeEditDialog();
  };

  // 檢查表單是否完整（所有必填欄位都已填寫）
  const isFormValid = () => {
    const requiredFields = {
      student_name: selectedStudent,
      environment: evaluation.environment,
      equipment: evaluation.equipment,
      stance: evaluation.stance,
      problem_phase: evaluation.problem_phase,
      problem_performance: evaluation.problem_performance,
      problem_movement: evaluation.problem_movement,
      improvement_movement: evaluation.improvement_movement,
      improvement_movement_detail: evaluation.improvement_movement_detail,
      comments: evaluation.comments
    };
    
    return Object.entries(requiredFields).every(([key, value]) => {
      if (key === 'improvement_movement_detail') {
        return Array.isArray(value) && value.length > 0;
      }
      return value && value.toString().trim() !== '';
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" gutterBottom>
            學生評量
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<CheckCircleIcon />}
              onClick={handleOpenCompletedBookings}
              sx={{ mb: 2 }}
            >
              已完成預約
            </Button>
            <Button
              variant="outlined"
              startIcon={<HistoryIcon />}
              onClick={handleOpenHistory}
              sx={{ mb: 2 }}
            >
              過往紀錄
            </Button>
          </Box>
        </Box>

        <Button
          variant="outlined"
          color="secondary"
          onClick={() => {
            setSelectedBooking('no_booking');
            setSelectedStudent('');
            setEvaluation({
              target: '',
              environment: '',
              equipment: '',
              stance: '',
              problem_phase: '',
              problem_performance: '',
              problem_movement: '',
              improvement_target: '',
              improvement_exercise: '',
              improvement_movement: '',
              improvement_movement_detail: [], // 改為空陣列
              comments: '',
              actionKey: [], // 添加 actionKey 重置
            });
          }}
          sx={{ mb: 2 }}
        >
          清除內容
        </Button>
        
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* 預約記錄選擇 */}
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <Typography gutterBottom>選擇預約記錄（僅顯示未完成）</Typography>
                {selectedBooking === 'no_booking' && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    已選擇無預約模式，請手動輸入學生姓名進行評估
                  </Alert>
                )}
                {bookingsLoading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2 }}>
                    <CircularProgress size={20} />
                    <Typography>載入預約記錄中...</Typography>
                  </Box>
                ) : (bookings || []).filter(booking => !booking.is_completed).length === 0 ? (
                  <Alert severity="info">
                    目前沒有未完成的預約記錄
                  </Alert>
                ) : (
                  <Select
                    value={selectedBooking}
                    onChange={handleBookingChange}
                    displayEmpty
                  >
                    <MenuItem value="" disabled>
                      請選擇預約記錄
                    </MenuItem>
                    <MenuItem value="no_booking">
                      <Box sx={{ width: '100%' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography variant="body1" fontWeight="bold">
                            無預約
                          </Typography>
                          <Chip 
                            label="無預約" 
                            size="small" 
                            color="info" 
                            icon={<CancelIcon />}
                          />
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          直接進行評估，不關聯特定預約
                        </Typography>
                      </Box>
                    </MenuItem>
                    {(bookings || [])
                      .filter(booking => !booking.is_completed)  // 只顯示未完成的
                      .map((booking) => (
                        <MenuItem key={booking.id} value={booking.id}>
                          <Box sx={{ width: '100%' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <Typography variant="body1" fontWeight="bold">
                                {booking.student_name}
                              </Typography>
                              <Chip 
                                label="未完成" 
                                size="small" 
                                color="warning" 
                                icon={<CancelIcon />}
                              />
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                              {booking.date} {booking.start_time}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              📍 {booking.location || '未知地點'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              已評估 {booking.evaluation_count} 次
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                  </Select>
                )}
              </FormControl>
            </Grid>

            {/* 預約完成狀態控制 */}
            {selectedBooking && selectedBooking !== 'no_booking' && (
              <Grid item xs={12}>
                <Card variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    預約評估狀態控制
                  </Typography>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="完成備註"
                        placeholder="可選：添加完成備註"
                        value={completionNotes}
                        onChange={(e) => setCompletionNotes(e.target.value)}
                        multiline
                        rows={2}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button
                          variant="contained"
                          color="success"
                          startIcon={<CheckCircleIcon />}
                          onClick={handleMarkCompleted}
                          size="small"
                        >
                          標記為完成
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </Card>
              </Grid>
            )}

            {/* 學生姓名 */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="學生姓名"
                placeholder="請輸入學生姓名"
                value={selectedStudent}
                onChange={(e) => setSelectedStudent(e.target.value)}
                helperText={
                  selectedBooking === 'no_booking' 
                    ? "無預約模式：請手動輸入學生姓名" 
                    : "選擇預約記錄後會自動填充，也可手動修改"
                }
              />
            </Grid>

            {/* 目標（可空白） */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="學生目標"
                placeholder="請輸入本次課程目標（可空白）"
                value={evaluation.target}
                onChange={(e) => handleEvaluationChange('target', e.target.value)}
              />
            </Grid>

            {/* 環境 */}
            <Grid item xs={12} md={6}>
              <Autocomplete
                freeSolo
                options={databaseOptions.environments.map((env) => env.name)}
                  value={evaluation.environment}
                onChange={(event, newValue) => {
                  handleEvaluationChange('environment', newValue || '');
                }}
                onInputChange={(event, newInputValue) => {
                  handleEvaluationChange('environment', newInputValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="環境"
                    required
                    placeholder="請選擇或輸入環境"
                  />
                )}
              />
            </Grid>

            {/* 裝備 */}
            <Grid item xs={12} md={6}>
              <Autocomplete
                freeSolo
                options={databaseOptions.equipment.map((eq) => eq.name)}
                  value={evaluation.equipment}
                onChange={(event, newValue) => {
                  handleEvaluationChange('equipment', newValue || '');
                }}
                onInputChange={(event, newInputValue) => {
                  handleEvaluationChange('equipment', newInputValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="裝備"
                    required
                    placeholder="請選擇或輸入裝備"
                  />
                )}
              />
            </Grid>

            {/* 站姿 */}
            <Grid item xs={12} md={6}>
              <Autocomplete
                freeSolo
                options={databaseOptions.stances.map((stance) => stance.name)}
                  value={evaluation.stance}
                onChange={(event, newValue) => {
                  handleEvaluationChange('stance', newValue || '');
                }}
                onInputChange={(event, newInputValue) => {
                  handleEvaluationChange('stance', newInputValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="站姿"
                    required
                    placeholder="請選擇或輸入站姿"
                  />
                )}
              />
            </Grid>

            {/* 問題-階段 */}
            <Grid item xs={12} md={6}>
              <Autocomplete
                freeSolo
                options={databaseOptions.problemPhases.map((phase) => phase.name)}
                  value={evaluation.problem_phase}
                onChange={(event, newValue) => {
                  handleEvaluationChange('problem_phase', newValue || '');
                }}
                onInputChange={(event, newInputValue) => {
                  handleEvaluationChange('problem_phase', newInputValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="問題-階段"
                    required
                    placeholder="請選擇或輸入問題階段"
                  />
                )}
              />
            </Grid>

            {/* 問題-表現 - 分層選擇 */}
            <Grid item xs={12} md={6}>
              <Autocomplete
                freeSolo
                options={(() => {
                  // 按技能類別分組的選項
                  const skillCategoryOrder = [
                    'position', 'balancing', 'rotation', 'counter_rotation', 
                    'inclination', 'angulation', 'reactive', 'proactive', 
                    'timing', 'coordination'
                  ];
                  
                  const groupedOptions = [];
                  
                  // 為每個技能類別創建分組
                  skillCategoryOrder.forEach(skillCategory => {
                    const performances = databaseOptions.problemPerformances.filter(
                      performance => performance.skill_category === skillCategory
                    );
                    
                    if (performances.length > 0) {
                      // 添加分組標題
                      const categoryLabel = {
                        'position': 'Position',
                        'balancing': 'Balancing',
                        'rotation': 'Rotation',
                        'counter_rotation': 'Counter Rotation',
                        'inclination': 'Inclination',
                        'angulation': 'Angulation',
                        'reactive': 'Reactive',
                        'proactive': 'Proactive',
                        'timing': 'Timing',
                        'coordination': 'Coordination'
                      }[skillCategory];
                      
                      groupedOptions.push({
                        type: 'group',
                        label: `--- ${categoryLabel} ---`,
                        skillCategory: skillCategory,
                        disabled: true
                      });
                      
                      // 添加該類別的表現項目，按 name 排序
                      performances
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .forEach(performance => {
                          groupedOptions.push({
                            type: 'option',
                            value: performance.name,
                            label: performance.name,
                            skillCategory: skillCategory,
                            performanceData: performance
                          });
                        });
                    }
                  });
                  
                  // 添加沒有技能類別的項目
                  const uncategorizedPerformances = databaseOptions.problemPerformances.filter(
                    performance => !performance.skill_category
                  );
                  
                  if (uncategorizedPerformances.length > 0) {
                    groupedOptions.push({
                      type: 'group',
                      label: '--- 其他 ---',
                      skillCategory: 'other',
                      disabled: true
                    });
                    
                    uncategorizedPerformances
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .forEach(performance => {
                        groupedOptions.push({
                          type: 'option',
                          value: performance.name,
                          label: performance.name,
                          skillCategory: 'other',
                          performanceData: performance
                        });
                      });
                  }
                  
                  return groupedOptions;
                })()}
                  value={evaluation.problem_performance}
                onChange={(event, newValue) => {
                  // 提取實際的值（可能是字符串或對象）
                  const actualValue = typeof newValue === 'string' ? newValue : newValue?.value || newValue?.label || '';
                  handleEvaluationChange('problem_performance', actualValue);
                }}
                onInputChange={(event, newInputValue) => {
                  handleEvaluationChange('problem_performance', newInputValue);
                }}
                filterOptions={(options, { inputValue }) => {
                  if (!inputValue) return options;
                  
                  const filtered = [];
                  const addedGroups = new Set();
                  
                  // 過濾選項
                  const matchingOptions = options.filter(option => 
                    option.type === 'option' && 
                    option.label.toLowerCase().includes(inputValue.toLowerCase())
                  );
                  
                  // 為每個匹配的選項添加其分組標題（如果還沒添加）
                  matchingOptions.forEach(option => {
                    const groupOption = options.find(opt => 
                      opt.type === 'group' && 
                      opt.skillCategory === option.skillCategory
                    );
                    
                    if (groupOption && !addedGroups.has(groupOption.label)) {
                      filtered.push(groupOption);
                      addedGroups.add(groupOption.label);
                    }
                    
                    filtered.push(option);
                  });
                  
                  return filtered;
                }}
                renderOption={(props, option) => {
                  if (option.type === 'group') {
                    return (
                      <Box 
                        {...props} 
                        key={option.label}
                        disabled={true}
                        sx={{ 
                          cursor: 'default !important',
                          backgroundColor: 'transparent !important',
                          '&:hover': {
                            backgroundColor: 'transparent !important'
                          },
                          '&.Mui-focused': {
                            backgroundColor: 'transparent !important'
                          },
                          '&.Mui-selected': {
                            backgroundColor: 'transparent !important'
                          }
                        }}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        onKeyDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                      >
                        <Typography 
                          variant="subtitle2" 
                          sx={{ 
                            fontWeight: 'bold', 
                            color: 'primary.main',
                            py: 1,
                            px: 2
                          }}
                        >
                          {option.label}
                        </Typography>
                      </Box>
                    );
                  }
                  
                  return (
                    <Box {...props} key={option.value} sx={{ pl: 2 }}>
                      <Typography variant="body2">
                        {option.label}
                      </Typography>
                    </Box>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="問題-表現"
                    required
                    placeholder="請選擇或輸入問題表現"
                  />
                )}
              />
            </Grid>

            {/* 問題-動作 - 分層選擇 */}
            <Grid item xs={12} md={6}>
              <Autocomplete
                freeSolo
                options={(() => {
                  // 按技能類別分組的選項
                  const skillCategoryOrder = [
                    'position', 'balancing', 'rotation', 'counter_rotation', 
                    'inclination', 'angulation', 'reactive', 'proactive', 
                    'timing', 'coordination'
                  ];
                  
                  const groupedOptions = [];
                  
                  // 為每個技能類別創建分組
                  skillCategoryOrder.forEach(skillCategory => {
                    const movements = databaseOptions.problemMovements.filter(
                      movement => movement.skill_category === skillCategory
                    );
                    
                    if (movements.length > 0) {
                      // 添加分組標題
                      const categoryLabel = {
                        'position': 'Position',
                        'balancing': 'Balancing',
                        'rotation': 'Rotation',
                        'counter_rotation': 'Counter Rotation',
                        'inclination': 'Inclination',
                        'angulation': 'Angulation',
                        'reactive': 'Reactive',
                        'proactive': 'Proactive',
                        'timing': 'Timing',
                        'coordination': 'Coordination'
                      }[skillCategory];
                      
                      groupedOptions.push({
                        type: 'group',
                        label: `--- ${categoryLabel} ---`,
                        skillCategory: skillCategory,
                        disabled: true
                      });
                      
                      // 添加該類別的動作項目，按 name 排序
                      movements
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .forEach(movement => {
                          groupedOptions.push({
                            type: 'option',
                            value: movement.name,
                            label: movement.name,
                            skillCategory: skillCategory,
                            movementData: movement
                          });
                        });
                    }
                  });
                  
                  // 添加沒有技能類別的項目
                  const uncategorizedMovements = databaseOptions.problemMovements.filter(
                    movement => !movement.skill_category
                  );
                  
                  if (uncategorizedMovements.length > 0) {
                    groupedOptions.push({
                      type: 'group',
                      label: '--- 其他 ---',
                      skillCategory: 'other',
                      disabled: true
                    });
                    
                    uncategorizedMovements
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .forEach(movement => {
                        groupedOptions.push({
                          type: 'option',
                          value: movement.name,
                          label: movement.name,
                          skillCategory: 'other',
                          movementData: movement
                        });
                      });
                  }
                  
                  return groupedOptions;
                })()}
                  value={evaluation.problem_movement}
                onChange={(event, newValue) => {
                  // 提取實際的值（可能是字符串或對象）
                  const actualValue = typeof newValue === 'string' ? newValue : newValue?.value || newValue?.label || '';
                  handleEvaluationChange('problem_movement', actualValue);
                }}
                onInputChange={(event, newInputValue) => {
                  handleEvaluationChange('problem_movement', newInputValue);
                }}
                filterOptions={(options, { inputValue }) => {
                  if (!inputValue) return options;
                  
                  const filtered = [];
                  const addedGroups = new Set();
                  
                  // 過濾選項
                  const matchingOptions = options.filter(option => 
                    option.type === 'option' && 
                    option.label.toLowerCase().includes(inputValue.toLowerCase())
                  );
                  
                  // 為每個匹配的選項添加其分組標題（如果還沒添加）
                  matchingOptions.forEach(option => {
                    const groupOption = options.find(opt => 
                      opt.type === 'group' && 
                      opt.skillCategory === option.skillCategory
                    );
                    
                    if (groupOption && !addedGroups.has(groupOption.label)) {
                      filtered.push(groupOption);
                      addedGroups.add(groupOption.label);
                    }
                    
                    filtered.push(option);
                  });
                  
                  return filtered;
                }}
                renderOption={(props, option) => {
                  if (option.type === 'group') {
                    return (
                      <Box 
                        {...props} 
                        key={option.label}
                        disabled={true}
                        sx={{ 
                          cursor: 'default !important',
                          backgroundColor: 'transparent !important',
                          '&:hover': {
                            backgroundColor: 'transparent !important'
                          },
                          '&.Mui-focused': {
                            backgroundColor: 'transparent !important'
                          },
                          '&.Mui-selected': {
                            backgroundColor: 'transparent !important'
                          }
                        }}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        onKeyDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                      >
                        <Typography 
                          variant="subtitle2" 
                          sx={{ 
                            fontWeight: 'bold', 
                            color: 'primary.main',
                            py: 1,
                            px: 2
                          }}
                        >
                          {option.label}
                        </Typography>
                      </Box>
                    );
                  }
                  
                  return (
                    <Box {...props} key={option.value} sx={{ pl: 2 }}>
                      <Typography variant="body2">
                        {option.label}
                      </Typography>
                    </Box>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="問題-動作"
                    required
                    placeholder="請選擇或輸入問題動作"
                  />
                )}
              />
            </Grid>
            
            {/* 改善目標（可空白） */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="改善目標"
                placeholder="請輸入本次課程改善目標（可空白）"
                value={evaluation.improvement_target}
                onChange={(e) => handleEvaluationChange('improvement_target', e.target.value)}
              />
            </Grid>

            {/* 改善-練習（可空白）- 分層選擇 */}
            <Grid item xs={12} md={6}>
              <Autocomplete
                freeSolo
                options={(() => {
                  // 按技能類別分組的選項
                  const skillCategoryOrder = [
                    'position', 'balancing', 'rotation', 'counter_rotation', 
                    'inclination', 'angulation', 'reactive', 'proactive', 
                    'timing', 'coordination'
                  ];
                  
                  const groupedOptions = [];
                  
                  // 為每個技能類別創建分組
                  skillCategoryOrder.forEach(skillCategory => {
                    const exercises = databaseOptions.improvementExercises.filter(
                      exercise => exercise.skill_category === skillCategory
                    );
                    
                    if (exercises.length > 0) {
                      // 添加分組標題
                      const categoryLabel = {
                        'position': 'Position',
                        'balancing': 'Balancing',
                        'rotation': 'Rotation',
                        'counter_rotation': 'Counter Rotation',
                        'inclination': 'Inclination',
                        'angulation': 'Angulation',
                        'reactive': 'Reactive',
                        'proactive': 'Proactive',
                        'timing': 'Timing',
                        'coordination': 'Coordination'
                      }[skillCategory];
                      
                      groupedOptions.push({
                        type: 'group',
                        label: `--- ${categoryLabel} ---`,
                        skillCategory: skillCategory,
                        disabled: true
                      });
                      
                      // 添加該類別的練習項目
                      exercises.forEach(exercise => {
                        groupedOptions.push({
                          type: 'option',
                          value: exercise.name,
                          label: exercise.name,
                          skillCategory: skillCategory
                        });
                      });
                    }
                  });
                  
                  // 添加沒有技能類別的項目
                  const uncategorizedExercises = databaseOptions.improvementExercises.filter(
                    exercise => !exercise.skill_category
                  );
                  
                  if (uncategorizedExercises.length > 0) {
                    groupedOptions.push({
                      type: 'group',
                      label: '--- 其他 ---',
                      skillCategory: 'other',
                      disabled: true
                    });
                    
                    uncategorizedExercises.forEach(exercise => {
                      groupedOptions.push({
                        type: 'option',
                        value: exercise.name,
                        label: exercise.name,
                        skillCategory: 'other'
                      });
                    });
                  }
                  
                  return groupedOptions;
                })()}
                  value={evaluation.improvement_exercise}
                onChange={(event, newValue) => {
                  // 只處理選項類型的選擇，忽略分組標題
                  if (newValue && typeof newValue === 'object' && newValue.type === 'option') {
                    handleEvaluationChange('improvement_exercise', newValue.value);
                  } else if (typeof newValue === 'string') {
                    handleEvaluationChange('improvement_exercise', newValue);
                  }
                  // 如果是分組標題，不處理選擇
                }}
                onInputChange={(event, newInputValue) => {
                  handleEvaluationChange('improvement_exercise', newInputValue);
                }}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') return option;
                  if (option && typeof option === 'object') return option.label || '';
                  return '';
                }}
                isOptionEqualToValue={(option, value) => {
                  if (typeof option === 'string' && typeof value === 'string') {
                    return option === value;
                  }
                  if (option && typeof option === 'object' && option.type === 'option') {
                    return option.value === value;
                  }
                  // 分組標題永遠不會等於任何值
                  return false;
                }}
                getOptionDisabled={(option) => {
                  // 禁用分組標題
                  return option && typeof option === 'object' && option.type === 'group';
                }}
                filterOptions={(options, { inputValue }) => {
                  if (!inputValue) {
                    return options; // 如果沒有輸入，返回所有選項
                  }
                  
                  // 過濾選項，只顯示匹配的選項和它們的分組標題
                  const filtered = [];
                  const addedGroups = new Set();
                  
                  // 先收集所有匹配的選項
                  const matchingOptions = options.filter(option => 
                    option.type === 'option' && 
                    option.label.toLowerCase().includes(inputValue.toLowerCase())
                  );
                  
                  // 為每個匹配的選項添加其分組標題（如果還沒添加）
                  matchingOptions.forEach(option => {
                    const groupOption = options.find(opt => 
                      opt.type === 'group' && 
                      opt.skillCategory === option.skillCategory
                    );
                    
                    if (groupOption && !addedGroups.has(groupOption.label)) {
                      filtered.push(groupOption);
                      addedGroups.add(groupOption.label);
                    }
                    
                    filtered.push(option);
                  });
                  
                  return filtered;
                }}
                renderOption={(props, option) => {
                  if (option.type === 'group') {
                    return (
                      <Box 
                        {...props} 
                        key={option.label}
                        disabled={true}
                        sx={{ 
                          cursor: 'default !important',
                          backgroundColor: 'transparent !important',
                          '&:hover': {
                            backgroundColor: 'transparent !important'
                          },
                          '&.Mui-focused': {
                            backgroundColor: 'transparent !important'
                          },
                          '&.Mui-selected': {
                            backgroundColor: 'transparent !important'
                          }
                        }}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        onKeyDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                      >
                        <Typography variant="subtitle2" sx={{ 
                          fontWeight: 'bold', 
                          color: 'primary.main',
                          py: 1,
                          px: 2,
                          pointerEvents: 'none',
                          userSelect: 'none'
                        }}>
                          {option.label}
                        </Typography>
                      </Box>
                    );
                  } else {
                    return (
                      <Box {...props} key={option.value}>
                        <Typography sx={{ pl: 2 }}>
                          {option.label}
                        </Typography>
                      </Box>
                    );
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="改善-練習（可空白）"
                    placeholder="請選擇或輸入練習方式"
                  />
                )}
              />
            </Grid>

            {/* 改善-動作 - 分層選擇 */}
            <Grid item xs={12} md={6}>
              <Autocomplete
                freeSolo
                options={(() => {
                  // 按技能類別分組的選項
                  const skillCategoryOrder = [
                    'position', 'balancing', 'rotation', 'counter_rotation', 
                    'inclination', 'angulation', 'reactive', 'proactive', 
                    'timing', 'coordination'
                  ];
                  
                  const groupedOptions = [];
                  
                  // 為每個技能類別創建分組
                  skillCategoryOrder.forEach(skillCategory => {
                    const movements = improvementsData.filter(
                      imp => imp.category_type === 'movement' && imp.skill_category === skillCategory
                    );
                    
                    if (movements.length > 0) {
                      // 添加分組標題
                      const categoryLabel = {
                        'position': 'Position',
                        'balancing': 'Balancing',
                        'rotation': 'Rotation',
                        'counter_rotation': 'Counter Rotation',
                        'inclination': 'Inclination',
                        'angulation': 'Angulation',
                        'reactive': 'Reactive',
                        'proactive': 'Proactive',
                        'timing': 'Timing',
                        'coordination': 'Coordination'
                      }[skillCategory];
                      
                      groupedOptions.push({
                        type: 'group',
                        label: `--- ${categoryLabel} ---`,
                        skillCategory: skillCategory,
                        disabled: true
                      });
                      
                      // 添加該類別的動作項目，按 effectiveness_level（難度）然後 category_name 排序
                      movements
                        .sort((a, b) => {
                          // 先按 effectiveness_level 排序（難度低的在前）
                          const levelA = a.effectiveness_level || 1;
                          const levelB = b.effectiveness_level || 1;
                          if (levelA !== levelB) {
                            return levelA - levelB;
                          }
                          // 難度相同時按 category_name 排序
                          return a.category_name.localeCompare(b.category_name);
                        })
                        .forEach(movement => {
                          const effectivenessLevel = movement.effectiveness_level || 1;
                          groupedOptions.push({
                            type: 'option',
                            value: movement.category_name,
                            label: `${movement.category_name}（${effectivenessLevel}級）`,
                            skillCategory: skillCategory,
                            movementData: movement
                          });
                        });
                    }
                  });
                  
                  // 添加沒有技能類別的項目
                  const uncategorizedMovements = improvementsData.filter(
                    imp => imp.category_type === 'movement' && !imp.skill_category
                  );
                  
                  if (uncategorizedMovements.length > 0) {
                    groupedOptions.push({
                      type: 'group',
                      label: '--- 其他 ---',
                      skillCategory: 'other',
                      disabled: true
                    });
                    
                    uncategorizedMovements
                      .sort((a, b) => a.category_name.localeCompare(b.category_name))
                      .forEach(movement => {
                        const effectivenessLevel = movement.effectiveness_level || 1;
                        groupedOptions.push({
                          type: 'option',
                          value: movement.category_name,
                          label: `${movement.category_name}（${effectivenessLevel}級）`,
                          skillCategory: 'other',
                          movementData: movement
                        });
                      });
                  }
                  
                  return groupedOptions;
                })()}
                  value={evaluation.improvement_movement}
                onChange={(event, newValue) => {
                  // 提取實際的值（可能是字符串或對象）
                  const actualValue = typeof newValue === 'string' ? newValue : newValue?.value || newValue?.label || '';
                  handleEvaluationChange('improvement_movement', actualValue);
                  // 當改善-動作改變時，自動載入相關的動作細節
                  if (actualValue) {
                    const matchedImprovement = improvementsData.find(
                      imp => imp.category_name === actualValue && imp.category_type === 'movement'
                    );
                    if (matchedImprovement && matchedImprovement.improvement_movement_details && matchedImprovement.improvement_movement_details.length > 0) {
                      // 載入動作細節時，保持原有的自訂內容，只添加新的預設選項
                      const currentDetails = evaluation.improvement_movement_detail || [];
                      const newDetails = matchedImprovement.improvement_movement_details.map(item => item.movement_detail.name);
                      // 合併現有內容和新選項，避免重複
                      const combinedDetails = [...new Set([...currentDetails, ...newDetails])];
                      handleEvaluationChange('improvement_movement_detail', combinedDetails);
                    } else {
                      // 如果沒有找到匹配的改善項目，保持現有內容不變
                      // handleEvaluationChange('improvement_movement_detail', []);
                    }
                  }
                }}
                onInputChange={(event, newInputValue) => {
                  handleEvaluationChange('improvement_movement', newInputValue);
                }}
                filterOptions={(options, { inputValue }) => {
                  if (!inputValue) return options;
                  
                  const filtered = [];
                  const addedGroups = new Set();
                  
                  // 過濾選項
                  const matchingOptions = options.filter(option => 
                    option.type === 'option' && 
                    option.label.toLowerCase().includes(inputValue.toLowerCase())
                  );
                  
                  // 為每個匹配的選項添加其分組標題（如果還沒添加）
                  matchingOptions.forEach(option => {
                    const groupOption = options.find(opt => 
                      opt.type === 'group' && 
                      opt.skillCategory === option.skillCategory
                    );
                    
                    if (groupOption && !addedGroups.has(groupOption.label)) {
                      filtered.push(groupOption);
                      addedGroups.add(groupOption.label);
                    }
                    
                    filtered.push(option);
                  });
                  
                  return filtered;
                }}
                renderOption={(props, option) => {
                  if (option.type === 'group') {
                    return (
                      <Box 
                        {...props} 
                        key={option.label}
                        disabled={true}
                        sx={{ 
                          cursor: 'default !important',
                          backgroundColor: 'transparent !important',
                          '&:hover': {
                            backgroundColor: 'transparent !important'
                          },
                          '&.Mui-focused': {
                            backgroundColor: 'transparent !important'
                          },
                          '&.Mui-selected': {
                            backgroundColor: 'transparent !important'
                          }
                        }}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        onKeyDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                      >
                        <Typography 
                          variant="subtitle2" 
                          sx={{ 
                            fontWeight: 'bold', 
                            color: 'primary.main',
                            py: 1,
                            px: 2
                          }}
                        >
                          {option.label}
                        </Typography>
                      </Box>
                    );
                  }
                  
                  return (
                    <Box {...props} key={option.value} sx={{ pl: 2 }}>
                      <Typography variant="body2">
                        {option.label}
                      </Typography>
                    </Box>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="改善-動作"
                    required
                    placeholder="請選擇或輸入改善動作"
                  />
                )}
              />
            </Grid>

            {/* 改善-動作細節 */}
            <Grid item xs={12}>
              <Autocomplete
                multiple
                freeSolo
                open={movementDetailOpen}
                onOpen={() => setMovementDetailOpen(true)}
                onClose={(event, reason) => {
                  // 只有在點擊外部、按 Escape、或按 Tab 時才關閉
                  if (reason === 'escapeKeyDown' || reason === 'blur' || reason === 'toggleInput') {
                    setMovementDetailOpen(false);
                  }
                  // 選擇選項後不關閉下拉選單
                }}
                options={getAllMovementDetailOptions()}
                value={evaluation.improvement_movement_detail || []}
                onChange={(event, newValue) => {
                  handleEvaluationChange('improvement_movement_detail', newValue);
                  // 選擇選項後保持下拉選單開啟
                  setMovementDetailOpen(true);
                }}
                onInputChange={(event, newInputValue) => {
                  // 當用戶在輸入框中輸入時，處理編輯邏輯
                  if (event && event.type === 'input') {
                    // 處理用戶輸入邏輯
                  }
                }}
                // 保持下拉選單開啟，讓用戶可以連續選擇
                disableCloseOnSelect={true}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={index}
                      label={option}
                      variant="outlined"
                      color="primary"
                      size="small"
                      sx={{ 
                        margin: '2px',
                        cursor: 'pointer',
                        '& .MuiChip-deleteIcon': {
                          color: 'primary.main'
                        },
                        '&:hover': {
                          backgroundColor: 'primary.light',
                          color: 'white'
                        }
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        // 當點擊 Chip 時，打開編輯對話框
                        openEditDialog(index, option);
                      }}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="改善-動作細節"
                    required
                    placeholder="請選擇或輸入動作細節（可多選、可編輯）"
                    helperText={evaluation.improvement_movement 
                      ? `已根據「${typeof evaluation.improvement_movement === 'string' ? evaluation.improvement_movement : evaluation.improvement_movement?.label || evaluation.improvement_movement?.value || '選定項目'}」篩選相關動作細節，您可以編輯或添加新內容。選擇完成後按 Enter 或點擊外部關閉選單。` 
                      : "選擇改善-動作後將自動篩選相關動作細節，您可以編輯或添加新內容。選擇完成後按 Enter 或點擊外部關閉選單。"}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {movementDetailOpen && (
                            <Button
                              size="small"
                              onClick={() => setMovementDetailOpen(false)}
                              sx={{ mr: 1, minWidth: 'auto', px: 1 }}
                            >
                              完成
                            </Button>
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>

            {/* 評語 */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                multiline
                rows={10}
                label="評語 *"
                placeholder="請輸入課程整體評語"
                value={evaluation.comments}
                onChange={(e) => handleEvaluationChange('comments', e.target.value)}
              />
            </Grid>

            {/* 按鈕區域 */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                {/* 預覽按鈕 */}
                <Button
                  onClick={handlePreview}
                  variant="contained"
                  color="primary"
                  size="large"
                  disabled={loading || !isFormValid()}
                  sx={{
                    minWidth: '200px',
                    backgroundColor: !isFormValid() ? '#e0e0e0' : undefined,
                    color: !isFormValid() ? '#9e9e9e' : undefined,
                    '&:hover': {
                      backgroundColor: !isFormValid() ? '#e0e0e0' : undefined,
                    }
                  }}
                >
                  {loading ? '生成中...' : (!isFormValid() ? '請填寫所有必填欄位' : '預覽')}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>

      {/* 過往紀錄對話框 */}
      <Dialog 
        open={openHistory} 
        onClose={handleCloseHistory}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            zIndex: 1300
          }
        }}
      >
        <DialogTitle>過往評估紀錄</DialogTitle>
        <DialogContent>
          {historyLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <Typography>載入中...</Typography>
            </Box>
          ) : evaluationHistory.length === 0 ? (
            <Typography color="text.secondary" align="center" sx={{ p: 3 }}>
              尚無過往紀錄
            </Typography>
          ) : (
            <List>
              {evaluationHistory.map((record, index) => (
                <React.Fragment key={record.id || index}>
                  <ListItem disablePadding>
                    <Box sx={{ display: 'flex', width: '100%', alignItems: 'center' }}>
                      <Tooltip 
                        title={createTooltipContent(record)}
                        placement="top"
                        arrow
                        PopperProps={{
                          style: { zIndex: 9999 },
                          modifiers: [
                            {
                              name: 'preventOverflow',
                              enabled: true,
                              options: {
                                boundary: 'viewport',
                              },
                            },
                            {
                              name: 'flip',
                              enabled: true,
                              options: {
                                fallbackPlacements: ['bottom'],
                              },
                            },
                          ],
                        }}
                        componentsProps={{
                          tooltip: {
                            sx: {
                              bgcolor: 'rgba(0, 0, 0, 0.9)',
                              color: 'white',
                              fontSize: '0.875rem',
                              maxWidth: '500px',
                              zIndex: 9999,
                              '& .MuiTooltip-arrow': {
                                color: 'rgba(0, 0, 0, 0.9)',
                              },
                            },
                          },
                        }}
                      >
                        <ListItemButton 
                          onClick={() => loadEvaluation(record)}
                          sx={{ flex: 1 }}
                        >
                          <ListItemText
                            primary={
                              <>
                                <Typography variant="h6" component="span" sx={{ marginRight: 1 }}>
                                  {record.student_name}
                                </Typography>
                                <Chip 
                                  label={formatDate(record.created_at)} 
                                  size="small" 
                                  color="primary" 
                                  variant="outlined"
                                />
                              </>
                            }
                            secondary={
                              <>
                                <span style={{ display: 'block', marginBottom: '4px', fontSize: '0.875rem', color: 'rgba(0, 0, 0, 0.6)' }}>
                                  <strong>目標：</strong>{record.target ? (record.target.length > 50 ? `${record.target.substring(0, 50)}...` : record.target) : '未設定'}
                                </span>
                                <span style={{ display: 'block', marginBottom: '4px', fontSize: '0.875rem', color: 'rgba(0, 0, 0, 0.6)' }}>
                                  <strong>環境：</strong>{record.environment ? (record.environment.length > 50 ? `${record.environment.substring(0, 50)}...` : record.environment) : '未設定'}
                                </span>
                                <span style={{ display: 'block', marginBottom: '4px', fontSize: '0.875rem', color: 'rgba(0, 0, 0, 0.6)' }}>
                                  <strong>裝備：</strong>{record.equipment ? (record.equipment.length > 50 ? `${record.equipment.substring(0, 50)}...` : record.equipment) : '未設定'}
                                </span>
                                <span style={{ display: 'block', fontSize: '0.875rem', color: 'rgba(0, 0, 0, 0.6)' }}>
                                  <strong>問題-階段：</strong>{record.problem_phase ? (record.problem_phase.length > 50 ? `${record.problem_phase.substring(0, 50)}...` : record.problem_phase) : '未設定'}
                                </span>
                              </>
                            }
                          />
                        </ListItemButton>
                      </Tooltip>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteRecord(record);
                        }}
                        color="error"
                        sx={{ ml: 1 }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </ListItem>
                  {index < evaluationHistory.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseHistory}>關閉</Button>
        </DialogActions>
      </Dialog>

      {/* 已完成預約管理對話框 */}
      <Dialog 
        open={openCompletedBookings} 
        onClose={handleCloseCompletedBookings}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>已完成預約管理</DialogTitle>
        <DialogContent>
          {completedBookingsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <Typography>載入中...</Typography>
            </Box>
          ) : completedBookings.length === 0 ? (
            <Typography color="text.secondary" align="center" sx={{ p: 3 }}>
              目前沒有已完成的預約記錄
            </Typography>
          ) : (
            <List>
              {completedBookings.map((booking, index) => (
                <React.Fragment key={booking.id}>
                  <ListItem>
                    <ListItemText
                      primary={
                        <>
                          <Typography variant="h6" component="span" sx={{ marginRight: 1 }}>
                            {booking.student_name}
                          </Typography>
                          <Chip 
                            label="已完成" 
                            size="small" 
                            color="success" 
                            icon={<CheckCircleIcon />}
                            sx={{ marginRight: 1 }}
                          />
                          {booking.completed_at && (
                            <Chip 
                              label={`完成於 ${booking.completed_at}`} 
                              size="small" 
                              color="primary" 
                              variant="outlined"
                            />
                          )}
                        </>
                      }
                      secondary={
                        <>
                          <span style={{ display: 'block', fontSize: '0.875rem', color: 'rgba(0, 0, 0, 0.6)' }}>
                            {booking.date} {booking.start_time} - {booking.location}
                          </span>
                          <span style={{ display: 'block', fontSize: '0.875rem', color: 'rgba(0, 0, 0, 0.6)' }}>
                            已評估 {booking.evaluation_count} 次
                          </span>
                          {booking.notes && (
                            <span style={{ display: 'block', fontSize: '0.875rem', color: 'rgba(0, 0, 0, 0.6)' }}>
                              備註：{booking.notes}
                            </span>
                          )}
                        </>
                      }
                    />
                    <Button
                      variant="outlined"
                      color="warning"
                      startIcon={<CancelIcon />}
                      onClick={() => handleMarkIncomplete(booking.id)}
                      size="small"
                    >
                      改為未完成
                    </Button>
                  </ListItem>
                  {index < completedBookings.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCompletedBookings}>關閉</Button>
        </DialogActions>
      </Dialog>

      {/* 刪除確認對話框 */}
      <Dialog
        open={openDeleteConfirm}
        onClose={cancelDelete}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          確認刪除
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            您確定要刪除學生「{recordToDelete?.student_name}」的評估紀錄嗎？
            此操作無法復原。
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDelete} disabled={deleteLoading}>
            取消
          </Button>
          <Button 
            onClick={confirmDelete} 
            color="error" 
            variant="contained"
            disabled={deleteLoading}
          >
            {deleteLoading ? '刪除中...' : '刪除'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 報告對話框 */}
      <Dialog 
        open={openReport} 
        onClose={handleCloseReport}
        maxWidth="md"
        fullWidth
      >
        <Box sx={{ p: 3, position: 'relative' }} ref={reportRef}>
          {/* ASA CJ Logo */}
          <Box sx={{ 
            position: 'absolute', 
            top: 16, 
            right: 16,
            display: 'flex',
            alignItems: 'center'
          }}>
            <img 
              src="/signature.png" 
              alt="ASA CJ Logo" 
              style={{
                height: '100px',
                width: 'auto',
                maxWidth: '300px'
              }}
            />
          </Box>
          
          <Typography variant="h5" gutterBottom align="center">
            25/26雪季課後報告
          </Typography>
          
          {/* 基本信息區域 */}
          <Box sx={{ mt: 3, mb: 2 }}>
            {(() => {
              const currentBooking = selectedBooking && selectedBooking !== 'no_booking' 
                ? bookings.find(booking => booking.id == selectedBooking) 
                : null;
              
              return (
                <>
                  {currentBooking?.date && (
                    <Typography>
                      <strong>日期：</strong> {new Date(currentBooking.date).toLocaleDateString('zh-TW')}
                    </Typography>
                  )}
                  {currentBooking?.location && (
                    <Typography>
                      <strong>雪場：</strong> {currentBooking.location}
                    </Typography>
                  )}
                  <Typography>
                    <strong>學生：</strong> {selectedStudent}
                  </Typography>
                  {evaluation.target && (
                    <Typography>
                      <strong>目標：</strong> {evaluation.target}
                    </Typography>
                  )}
                </>
              );
            })()}
          </Box>
          
          <Box sx={{ mt: 2 }}>
            <Typography><strong>環境：</strong> {evaluation.environment}</Typography>
            <Typography><strong>裝備：</strong> {evaluation.equipment}</Typography>
            <Typography><strong>站姿：</strong> {evaluation.stance}</Typography>
          </Box>
          
          <Box sx={{ mt: 2 }}>
            <Typography><strong>問題-階段：</strong> {evaluation.problem_phase}</Typography>
            <Typography><strong>問題-表現：</strong> {evaluation.problem_performance}</Typography>
            <Typography><strong>問題-動作：</strong> {evaluation.problem_movement}</Typography>
          </Box>
          
          <Box sx={{ mt: 2 }}>
            {evaluation.improvement_target && (
              <Typography><strong>改善目標：</strong> {evaluation.improvement_target}</Typography>
            )}
            {evaluation.improvement_exercise && (
              <Typography><strong>改善-練習：</strong> {evaluation.improvement_exercise}</Typography>
            )}
            <Typography><strong>改善-動作：</strong> {evaluation.improvement_movement}</Typography>
            <Typography><strong>改善-動作細節：</strong></Typography>
            {(evaluation.improvement_movement_detail || [])
              .filter(detail => detail.trim() !== '')
              .map((detail, index) => (
                <Typography key={index} sx={{ pl: 2 }}>• {detail}</Typography>
              ))
            }
            <Typography sx={{ mt: 2 }}><strong>評語：</strong></Typography>
            <Typography component="div" sx={{ pl: 2, whiteSpace: 'pre-wrap' }}>{evaluation.comments}</Typography>
          </Box>
        </Box>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'center', gap: 2 }}>
          <Button 
            variant="contained" 
            color="primary"
            onClick={handleSaveAndScreenshot}
            disabled={loading}
            sx={{ minWidth: '120px' }}
          >
            {loading ? '保存中...' : '保存並截圖'}
          </Button>
          <Button 
            variant="outlined" 
            onClick={handleCloseReport}
            sx={{ minWidth: '80px' }}
          >
            關閉
          </Button>
        </Box>
      </Dialog>

      {/* 編輯動作細節對話框 */}
      <Dialog 
        open={editDialogOpen} 
        onClose={closeEditDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
          編輯動作細節
        </DialogTitle>
        <DialogContent>
          {/*
          <DialogContentText sx={{ fontSize: '1.1rem', mb: 2 }}>
            請編輯以下動作細節內容：
          </DialogContentText>
          */}
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={6}
            value={editingValue}
            onChange={(e) => setEditingValue(e.target.value)}
            placeholder="請輸入動作細節內容..."
            sx={{
              '& .MuiInputBase-input': {
                fontSize: '1.2rem',
                lineHeight: 1.6
              },
              '& .MuiInputLabel-root': {
                fontSize: '1.1rem'
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={closeEditDialog}
            variant="outlined"
            sx={{ fontSize: '1.1rem', px: 3, py: 1 }}
          >
            取消
          </Button>
          <Button 
            onClick={saveEdit}
            variant="contained"
            sx={{ fontSize: '1.1rem', px: 3, py: 1 }}
          >
            保存
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar 通知 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <MuiAlert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
};

export default StudentEvaluation;
